import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ElementType,
} from 'react';
import type {
  CallBackProps,
  Step,
  Styles,
  TooltipRenderProps,
} from 'react-joyride';
import Joyride, { ACTIONS, STATUS } from 'react-joyride';

function checkElementVisible(
  currentElement: Element | null,
  { minWidth = 0, minHeight = 0 }: { minWidth?: number, minHeight?: number } = {}
) {
  if (!currentElement) return false;
  const rect = currentElement.getBoundingClientRect();
  // 检查元素是否可见：宽或高大于 0，且在视口范围内
  const visible = 
    rect.width >= minWidth &&
    rect.height >= minHeight &&
    rect.top < window.innerHeight &&
    rect.bottom > 0 &&
    rect.left < window.innerWidth &&
    rect.right > 0;
  return visible;
}

// 统一的存储键名
const INTRO_STORAGE_KEY = 'intro-data';

// 定义存储数据的类型
interface IntroStorageData {
  completedPriorities: number[];
  currentPriority: number | null;
  shownSteps: Record<string, boolean>;
}

// 统一的localStorage管理器
class IntroStorageManager {
  private static readonly STORAGE_KEY = INTRO_STORAGE_KEY;

  // 获取完整的存储数据
  private static getData(): IntroStorageData {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (data) {
        return JSON.parse(data);
      }
    } catch (error) {
      console.warn('Failed to parse intro storage data:', error);
    }

    // 返回默认数据
    return {
      completedPriorities: [],
      currentPriority: null,
      shownSteps: {},
    };
  }

  // 保存完整的存储数据
  private static setData(data: IntroStorageData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save intro storage data:', error);
    }
  }

  // 获取已完成的优先级
  static getCompletedPriorities(): number[] {
    return this.getData().completedPriorities;
  }

  // 添加已完成的优先级
  static addCompletedPriority(priority: number): void {
    const data = this.getData();
    if (!data.completedPriorities.includes(priority)) {
      data.completedPriorities.push(priority);
      this.setData(data);
    }
  }

  // 获取当前优先级
  static getCurrentPriority(): number | null {
    return this.getData().currentPriority;
  }

  // 设置当前优先级
  static setCurrentPriority(priority: number | null): void {
    const data = this.getData();
    data.currentPriority = priority;
    this.setData(data);
  }

  // 检查步骤是否已显示
  static isStepShown(selector: string): boolean {
    return this.getData().shownSteps[selector] || false;
  }

  // 标记步骤为已显示
  static markStepAsShown(selector: string): void {
    const data = this.getData();
    data.shownSteps[selector] = true;
    this.setData(data);
  }

  // 清除所有数据
  static clear(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }
}

export interface IntroRef {
  start: (steps: Step[], styles?: Styles) => void;
}

const formatSteps = (steps: (Step & { priority: number })[]) => {
  return steps.map(step => ({
    ...step,
    disableBeacon: true,
  }));
};

type IntroStyles = Partial<Styles & { backgroundColor: string }>;

export const Intro = forwardRef<
  IntroRef,
  {
    steps?: (Step & { priority: number, minWidth?: number, minHeight?: number })[];
    styles?: IntroStyles;
    content?: ElementType<TooltipRenderProps>;
  }
>(({ steps, styles }, ref) => {
  steps = useMemo(() => formatSteps(steps || []), [steps]);
  const [joyrideRun, setJoyrideRun] = useState(false);
  const [joyrideSteps, setJoyrideSteps] = useState([]);
  // 记录当前正在处理的优先级
  const [currentPriorityLevel, setCurrentPriorityLevel] = useState<
    number | null
  >(null);
  // 记录已完成的步骤
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

  // 使用 useRef 来避免闭包问题
  const currentPriorityRef = useRef<number | null>(null);
  const completedStepsRef = useRef<Set<string>>(new Set());

  // 同步 ref 和 state
  currentPriorityRef.current = currentPriorityLevel;
  completedStepsRef.current = completedSteps;

  // 从localStorage获取已完成的优先级
  const getCompletedPriorities = useCallback(() => {
    return IntroStorageManager.getCompletedPriorities();
  }, []);

  // 保存已完成的优先级到localStorage
  const saveCompletedPriority = useCallback((priority: number) => {
    IntroStorageManager.addCompletedPriority(priority);
  }, []);

  // 从localStorage获取当前优先级
  const getCurrentPriorityFromStorage = useCallback(() => {
    return IntroStorageManager.getCurrentPriority();
  }, []);

  // 保存当前优先级到localStorage
  const saveCurrentPriority = useCallback((priority: number | null) => {
    IntroStorageManager.setCurrentPriority(priority);
  }, []);

  useEffect(() => {
    // 如果document不存在，则不执行
    if (!document?.querySelector) {
      return;
    }

    // 用于存储所有目标元素的状态
    const targetElements = new Map();

    // 获取当前应该处理的步骤列表，并根据priority排序（优先级高的先运行）
    const currentStepsToProcess = steps
      ? [...steps].sort((a, b) => b.priority - a.priority)
      : [];

    // 如果没有步骤需要处理，则不执行
    if (!currentStepsToProcess.length) {
      return;
    }

    // 获取所有有效的目标选择器
    const targetSelectors = currentStepsToProcess
      .map(step => (typeof step.target === 'string' ? step.target : ''))
      .filter(Boolean);

    if (!targetSelectors.length) {
      return; // 如果没有有效的目标选择器，则不执行
    }

    // 创建一个函数来检查所有目标元素
    const checkTargetElements = () => {
      // 获取当前优先级（使用 ref 避免闭包问题）
      const currentPriority = currentPriorityRef.current;

      // 获取已完成的优先级
      const completedPriorities = getCompletedPriorities();

      // 获取当前已完成的步骤（使用 ref 避免闭包问题）
      const currentCompletedSteps = completedStepsRef.current;

      let anyTargetExists = false;
      let currentExitStep: Step | null = null;

      // 只检查当前优先级的步骤
      const currentPrioritySteps = currentStepsToProcess.filter(
        step =>
          step.priority === currentPriority &&
          !currentCompletedSteps.has(String(step.target)) &&
          !completedPriorities.includes(step.priority)
      );

      // 如果当前优先级的所有步骤都已完成，则移动到下一个优先级
      if (currentPrioritySteps.length === 0 && currentPriority !== null) {
        // 当前优先级的所有步骤已完成，将其添加到已完成的优先级列表
        saveCompletedPriority(currentPriority);

        // 找到下一个优先级
        const nextPriorityLevel = currentStepsToProcess.find(
          step =>
            step.priority < currentPriority &&
            !currentCompletedSteps.has(String(step.target)) &&
            !completedPriorities.includes(step.priority)
        )?.priority;

        setCurrentPriorityLevel(nextPriorityLevel || null);
        saveCurrentPriority(nextPriorityLevel || null);
        return; // 等待下一个检查周期
      }

      // 检查当前优先级的每个目标选择器
      currentPrioritySteps.forEach(step => {
        const selector = typeof step.target === 'string' ? step.target : '';

        if (selector) {
          const isShown = IntroStorageManager.isStepShown(selector);

          if (!isShown) {
            const currentElement = document.querySelector(selector);
            const isVisible = checkElementVisible(currentElement, {
              minWidth: step.minWidth,
              minHeight: step.minHeight,
            });
            const previousElement = targetElements.get(selector);
            if (currentElement && isVisible) {
              // 元素存在且完全展开
              anyTargetExists = true;
              currentExitStep = step;
              if (!previousElement) {
                // 元素首次出现
                targetElements.set(selector, currentElement);
              }
            } else if (previousElement) {
              // 元素之前存在但现在不存在了或未完全展开
              targetElements.set(selector, null);
            }
          }
        }
      });
      // 根据是否有任何目标元素存在来设置Joyride状态
      setJoyrideRun(anyTargetExists);
      currentExitStep && setJoyrideSteps([currentExitStep]);
    };

    // 添加定时检查机制，防止漏检测（如CSS动画完成后）
    const intervalId = setInterval(() => {
      checkTargetElements();
    }, 500); // 每500ms检查一次

    // 清理函数
    return () => {
      clearInterval(intervalId); // 清理定时器
    };
  }, [steps]);

  // 单独的 useEffect 来初始化状态，避免在主 useEffect 中更新状态
  useEffect(() => {
    if (!steps?.length) return;

    const currentStepsToProcess = [...steps].sort(
      (a, b) => b.priority - a.priority
    );

    // 初始化当前优先级（从localStorage获取或使用最高优先级）
    const storedPriority = getCurrentPriorityFromStorage();
    if (storedPriority !== null) {
      setCurrentPriorityLevel(storedPriority);
    } else if (currentStepsToProcess.length > 0) {
      const highestPriority = currentStepsToProcess[0].priority;
      setCurrentPriorityLevel(highestPriority);
      saveCurrentPriority(highestPriority);
    }

    // 初始化已完成的步骤
    const newCompletedSteps = new Set<string>();
    currentStepsToProcess.forEach(step => {
      const selector = typeof step.target === 'string' ? step.target : '';
      if (selector && IntroStorageManager.isStepShown(selector)) {
        newCompletedSteps.add(selector);
      }
    });
    setCompletedSteps(newCompletedSteps);
  }, [steps, getCurrentPriorityFromStorage, saveCurrentPriority]);

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const finishTooltip = () => {
      setJoyrideRun(false);
      const selector = String(data.step.target);
      IntroStorageManager.markStepAsShown(selector);

      // 将完成的步骤添加到completedSteps集合中
      setCompletedSteps(prev => {
        const newSet = new Set(prev);
        newSet.add(selector);
        return newSet;
      });
    };
    const { status, action } = data;
    if (action === ACTIONS.CLOSE) {
      setJoyrideRun(false);
      finishTooltip();
    }
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    if (finishedStatuses.includes(status)) {
      setJoyrideRun(false);

      // 使用统一的存储管理器标记步骤为已显示
      finishTooltip();
    }
  }, []);

  return (
    <Joyride
      run={joyrideRun}
      callback={handleJoyrideCallback}
      steps={joyrideSteps}
      hideBackButton={true}
      spotlightPadding={0}
      floaterProps={{
        styles: {
          arrow: {
            length: 8,
            spread: 18,
          },
        },
      }}
    />
  );
});
